import toast from 'react-hot-toast';

/**
 * Toast notification utilities with consistent styling and messages
 */

// Toast configuration
const toastConfig = {
  duration: 4000,
  position: 'top-right',
  style: {
    borderRadius: '10px',
    background: '#333',
    color: '#fff',
    fontSize: '14px',
    fontWeight: '500',
    padding: '12px 16px',
    maxWidth: '400px',
  },
};

// Success toast with custom styling
export const showSuccessToast = (message, options = {}) => {
  return toast.success(message, {
    ...toastConfig,
    style: {
      ...toastConfig.style,
      background: '#10b981',
      color: '#fff',
    },
    iconTheme: {
      primary: '#fff',
      secondary: '#10b981',
    },
    ...options,
  });
};

// Error toast with custom styling
export const showErrorToast = (message, options = {}) => {
  return toast.error(message, {
    ...toastConfig,
    style: {
      ...toastConfig.style,
      background: '#ef4444',
      color: '#fff',
    },
    iconTheme: {
      primary: '#fff',
      secondary: '#ef4444',
    },
    ...options,
  });
};

// Loading toast
export const showLoadingToast = (message, options = {}) => {
  return toast.loading(message, {
    ...toastConfig,
    style: {
      ...toastConfig.style,
      background: '#3b82f6',
      color: '#fff',
    },
    ...options,
  });
};

// Info toast
export const showInfoToast = (message, options = {}) => {
  return toast(message, {
    ...toastConfig,
    style: {
      ...toastConfig.style,
      background: '#3b82f6',
      color: '#fff',
    },
    icon: 'ℹ️',
    ...options,
  });
};

// Warning toast
export const showWarningToast = (message, options = {}) => {
  return toast(message, {
    ...toastConfig,
    style: {
      ...toastConfig.style,
      background: '#f59e0b',
      color: '#fff',
    },
    icon: '⚠️',
    ...options,
  });
};

// Custom toast with promise handling
export const showPromiseToast = (promise, messages, options = {}) => {
  return toast.promise(
    promise,
    {
      loading: messages.loading || 'Loading...',
      success: messages.success || 'Success!',
      error: messages.error || 'Something went wrong!',
    },
    {
      ...toastConfig,
      success: {
        style: {
          ...toastConfig.style,
          background: '#10b981',
        },
      },
      error: {
        style: {
          ...toastConfig.style,
          background: '#ef4444',
        },
      },
      loading: {
        style: {
          ...toastConfig.style,
          background: '#3b82f6',
        },
      },
      ...options,
    }
  );
};

// Dismiss all toasts
export const dismissAllToasts = () => {
  toast.dismiss();
};

// Dismiss specific toast
export const dismissToast = (toastId) => {
  toast.dismiss(toastId);
};

// Authentication specific toast messages
export const authToasts = {
  loginSuccess: (userName) => showSuccessToast(`Welcome back, ${userName}! 🎉`),
  loginError: (error) => showErrorToast(error || 'Login failed. Please check your credentials.'),
  loginLoading: () => showLoadingToast('Signing you in...'),
  
  registerSuccess: (userName) => showSuccessToast(`Welcome to DeviceLink Global, ${userName}! 🎉`),
  registerError: (error) => showErrorToast(error || 'Registration failed. Please try again.'),
  registerLoading: () => showLoadingToast('Creating your account...'),
  
  logoutSuccess: () => showSuccessToast('You have been logged out successfully.'),
  
  forgotPasswordSuccess: () => showSuccessToast('Password reset code sent to your email! 📧'),
  forgotPasswordError: (error) => showErrorToast(error || 'Failed to send reset code.'),
  forgotPasswordLoading: () => showLoadingToast('Sending reset code...'),
  
  otpVerifySuccess: () => showSuccessToast('Code verified successfully! ✅'),
  otpVerifyError: (error) => showErrorToast(error || 'Invalid or expired code.'),
  otpVerifyLoading: () => showLoadingToast('Verifying code...'),
  
  passwordResetSuccess: () => showSuccessToast('Password reset successfully! You can now login. 🔐'),
  passwordResetError: (error) => showErrorToast(error || 'Failed to reset password.'),
  passwordResetLoading: () => showLoadingToast('Resetting password...'),
  
  profileUpdateSuccess: () => showSuccessToast('Profile updated successfully! ✨'),
  profileUpdateError: (error) => showErrorToast(error || 'Failed to update profile.'),
  profileUpdateLoading: () => showLoadingToast('Updating profile...'),
  
  tokenExpired: () => showWarningToast('Your session has expired. Please login again.'),
  unauthorized: () => showErrorToast('You are not authorized to perform this action.'),
};

// IMEI specific toast messages
export const imeiToasts = {
  checkSuccess: (imei) => showSuccessToast(`IMEI ${imei} checked successfully! ✅`),
  checkError: (error) => showErrorToast(error || 'IMEI check failed. Please try again.'),
  checkLoading: (imei) => showLoadingToast(`Checking IMEI ${imei}...`),
  
  historyLoaded: () => showSuccessToast('IMEI history loaded successfully!'),
  historyError: (error) => showErrorToast(error || 'Failed to load IMEI history.'),
  historyLoading: () => showLoadingToast('Loading IMEI history...'),
  
  invalidImei: () => showErrorToast('Please enter a valid 15-digit IMEI number.'),
  insufficientCredits: () => showWarningToast('Insufficient credits. Please upgrade your plan.'),
};

// Subscription specific toast messages
export const subscriptionToasts = {
  upgradeSuccess: (plan) => showSuccessToast(`Successfully upgraded to ${plan} plan! 🚀`),
  upgradeError: (error) => showErrorToast(error || 'Failed to upgrade subscription.'),
  upgradeLoading: () => showLoadingToast('Processing upgrade...'),
  
  cancelSuccess: () => showSuccessToast('Subscription cancelled successfully.'),
  cancelError: (error) => showErrorToast(error || 'Failed to cancel subscription.'),
  
  renewSuccess: () => showSuccessToast('Subscription renewed successfully! 🎉'),
  renewError: (error) => showErrorToast(error || 'Failed to renew subscription.'),
  
  paymentSuccess: () => showSuccessToast('Payment processed successfully! 💳'),
  paymentError: (error) => showErrorToast(error || 'Payment failed. Please try again.'),
};

// General application toast messages
export const appToasts = {
  networkError: () => showErrorToast('Network error. Please check your connection.'),
  serverError: () => showErrorToast('Server error. Please try again later.'),
  validationError: (message) => showErrorToast(message || 'Please check your input.'),
  
  copySuccess: (text) => showSuccessToast(`${text} copied to clipboard! 📋`),
  downloadSuccess: (fileName) => showSuccessToast(`${fileName} downloaded successfully! 📥`),
  downloadError: () => showErrorToast('Download failed. Please try again.'),
  
  saveSuccess: () => showSuccessToast('Changes saved successfully! ✅'),
  saveError: () => showErrorToast('Failed to save changes.'),
  
  deleteSuccess: (item) => showSuccessToast(`${item} deleted successfully!`),
  deleteError: () => showErrorToast('Failed to delete item.'),
  
  updateAvailable: () => showInfoToast('A new version is available. Please refresh the page.'),
  maintenanceMode: () => showWarningToast('System is under maintenance. Some features may be unavailable.'),
};

// Error message parser for API responses
export const parseApiError = (error) => {
  if (typeof error === 'string') {
    return error;
  }
  
  if (error?.response?.data) {
    const data = error.response.data;
    
    // Handle validation errors
    if (data.errors) {
      if (typeof data.errors === 'object') {
        const firstError = Object.values(data.errors)[0];
        return Array.isArray(firstError) ? firstError[0] : firstError;
      }
      return data.errors;
    }
    
    // Handle message field
    if (data.message) {
      return data.message;
    }
    
    // Handle detail field (DRF format)
    if (data.detail) {
      return data.detail;
    }
  }
  
  // Handle network errors
  if (error?.code === 'NETWORK_ERROR') {
    return 'Network error. Please check your connection.';
  }
  
  // Handle timeout errors
  if (error?.code === 'ECONNABORTED') {
    return 'Request timeout. Please try again.';
  }
  
  // Default error message
  return error?.message || 'An unexpected error occurred. Please try again.';
};

// Toast with automatic error parsing
export const showApiErrorToast = (error) => {
  const message = parseApiError(error);
  return showErrorToast(message);
};

// Export default toast for direct usage
export default toast;
